/*
 * MySchoolRx Login.
 *
 * Required-login version (Phase 1.5). The magic-link URL approach is removed.
 * Super Admin approves a pending signup → temp password is generated and
 * shared via the approval toast (or queried from notification_log). User
 * arrives here, signs in, and is redirected to /diagnostic.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { LoginCarousel } from "./LoginCarousel";
import { markBrowserSession } from "@/lib/sessionPersistence";
import "./login.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// Status-based portfolio landing (Part 2): send Portfolio Owners to the right
// onboarding step instead of always defaulting to the dashboard.
export function portfolioLanding(me) {
  if (!me || !me.portfolio) return "/portfolio/onboarding";
  const q = me.quote;
  if (!q) return "/portfolio/onboarding";
  const finalized =
    q.payment_status === "paid" ||
    ["PO Accepted", "PO Received", "Provisioned", "Paid", "Active"].includes(q.status);
  if (finalized) return "/portfolio/dashboard";
  return "/portfolio/vendor-po";
}

const emailErr = (v) => (/^\S+@\S+\.\S+$/.test(v.trim()) ? "" : "Valid email is required");

const DiagnosticLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [errs, setErrs] = useState({ email: "", password: "" });
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    document.title = "Sign In | MySchoolRx";
    // Pre-fill email if passed via query string from approval workflow
    const qs = new URLSearchParams(location.search);
    const e = qs.get("email");
    if (e) setEmail(e);
    // If we already have a saved session, jump straight in
    try {
      const sess = JSON.parse(localStorage.getItem("diag_session") || "null");
      if (sess?.user?.id && sess?.tenant?.id) {
        navigate("/diagnostic", { replace: true });
      }
    } catch {}
  }, [navigate, location.search]);

  const submit = async (e) => {
    e.preventDefault();
    const v = { email: emailErr(email), password: password ? "" : "Password is required" };
    setErrs(v);
    if (v.email || v.password) {
      document.getElementById(v.email ? "diag-login-email-input" : "diag-login-password-input")?.focus();
      return;
    }
    setSubmitting(true);
    // Platform accounts (db.users: portfolio_owner / admin / super_admin / client)
    // are resolved FIRST so they are never shadowed by a same-email school tenant
    // user. School tenant users (only in tenant_users) fail this and fall through
    // to tenant-auth below.
    try {
      const pr = await axios.post(
        `${API}/api/auth/login`,
        { email, password, remember },
        { withCredentials: true }
      );
      const role = (pr.data?.role || "").toLowerCase();
      // Platform login succeeded → clear any stale school session so the UI can
      // never render a school identity while the backend authorizes as this
      // platform user (dual-realm identity leak guard).
      try {
        localStorage.removeItem("diag_session");
        sessionStorage.removeItem("impersonation_token");
        sessionStorage.removeItem("impersonation_email");
        sessionStorage.removeItem("impersonation_mode");
      } catch {}
      toast.success(`Welcome back, ${pr.data?.name || email}`);
      const nextUrl = new URLSearchParams(location.search).get("next");
      if (pr.data?.must_change_password || pr.data?.password_reset_required) {
        navigate("/change-password", { replace: true });
      } else if (nextUrl) {
        navigate(nextUrl, { replace: true });
      } else if (role === "sales") {
        navigate("/sales", { replace: true });
      } else if (role === "portfolio_owner") {
        // Status-based landing (Part 2): route to the correct onboarding step.
        let dest = "/portfolio/dashboard";
        try {
          const me = await axios.get(`${API}/api/onboarding/me`, { withCredentials: true });
          dest = portfolioLanding(me.data);
        } catch {}
        navigate(dest, { replace: true });
      } else {
        navigate("/app", { replace: true });
      }
      return;
    } catch (platformErr) {
      // Not a platform account — try the school tenant workspace login.
      try {
        const r = await axios.post(`${API}/api/tenant-auth/login`, {
          username: email,
          password,
          remember,
        });
        // School login succeeded → destroy any ambient platform session cookie
        // FIRST (it is httpOnly, so only the server can clear it). This prevents
        // cookie-based endpoints from authorizing as a leftover platform user
        // (e.g. an admin) while the workspace operates as this school user.
        try { await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
        const sess = {
          user: r.data.user,
          tenant: r.data.tenant,
          session_token: r.data.session_token,
          remember,
          loginAt: new Date().toISOString(),
        };
        if (!remember) markBrowserSession();
        localStorage.setItem("diag_session", JSON.stringify(sess));
        // Clear any leftover impersonation artifacts from a prior session so a
        // stale identity can never override this login.
        try {
          sessionStorage.removeItem("impersonation_token");
          sessionStorage.removeItem("impersonation_email");
          sessionStorage.removeItem("impersonation_mode");
        } catch {}
        toast.success(`Welcome back, ${r.data.user.full_name}`);
        if (r.data.must_change_password) {
          navigate("/change-password", { replace: true });
        } else {
          navigate("/diagnostic", { replace: true });
        }
      } catch (tenantErr) {
        toast.error(
          tenantErr?.response?.data?.detail ||
            platformErr?.response?.data?.detail ||
            "Login failed"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Email is required");
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: resetEmail.trim() });
      setResetSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="msx-login" data-testid="diag-login">
      <main className="stage">
        <div className="bg" aria-hidden="true" />
        <form onSubmit={submit} noValidate className="card" data-testid="diag-login-form">
          <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-9 w-auto" />
          <h1>Sign in to your workspace</h1>
          <label htmlFor="diag-login-email-input" className="lbl">Email</label>
          <input id="diag-login-email-input" type="email" value={email} className="in"
            onChange={(e) => { setEmail(e.target.value); if (errs.email) setErrs((x) => ({ ...x, email: "" })); }}
            onBlur={() => email && setErrs((x) => ({ ...x, email: emailErr(email) }))}
            autoComplete="username" aria-invalid={!!errs.email} aria-describedby="diag-login-email-err" data-testid="diag-login-email" />
          <div className="msg" id="diag-login-email-err" role="alert" data-testid="diag-login-email-error">{errs.email}</div>
          <label htmlFor="diag-login-password-input" className="lbl">Password</label>
          <div className="pw">
            <input id="diag-login-password-input" type={showPw ? "text" : "password"} value={password} className="in"
              onChange={(e) => { setPassword(e.target.value); if (errs.password) setErrs((x) => ({ ...x, password: "" })); }}
              autoComplete="current-password" aria-invalid={!!errs.password} aria-describedby="diag-login-password-err" data-testid="diag-login-password" />
            <button type="button" className="eye" aria-controls="diag-login-password-input" aria-pressed={showPw}
              aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw((v) => !v)} data-testid="diag-login-password-toggle">
              {showPw ? "Hide" : "Show"}
            </button>
          </div>
          <div className="msg" id="diag-login-password-err" role="alert" data-testid="diag-login-password-error">{errs.password}</div>
          <div className="row">
            <label>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="diag-login-remember" /> Remember me
            </label>
            <button type="button" className="linkbtn" data-testid="diag-login-forgot-link"
              onClick={() => { setShowReset(true); setResetEmail(email); setResetSent(false); }}>
              Forgot password?
            </button>
          </div>
          <button type="submit" className="submit" disabled={submitting} data-testid="diag-login-submit">
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="new">New to MySchoolRx? <a href="/myschoolrx/pilot" data-testid="diag-login-create-link">Create your workspace</a></p>
          <div className="foot">© {new Date().getFullYear()} MySchoolRx<br /><a href="/terms">Terms</a>·<a href="/privacy">Privacy</a></div>
        </form>
        <LoginCarousel />
      </main>

      <Dialog open={showReset} onOpenChange={(v) => !v && setShowReset(false)}>
        <DialogContent className="max-w-md bg-white" data-testid="diag-login-reset-modal">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
          </DialogHeader>
          {resetSent ? (
            <div className="space-y-3" data-testid="diag-login-reset-success">
              <div className="rounded-lg border border-[#a7f3d0] bg-[#ecfdf5] p-3 text-[13px] text-[#065f46]">
                If an account exists for <strong>{resetEmail}</strong>, a password reset link is on its way.
              </div>
              <ul className="text-[12px] text-[#6b7280] list-disc pl-4 space-y-1">
                <li>Delivery usually takes a couple of minutes.</li>
                <li>If you don't see it, check your spam or junk folder.</li>
                <li>The link works once and expires in 1 hour — you can request another in about a minute.</li>
                <li>Still nothing? Contact your administrator for a secure reset.</li>
              </ul>
              <DialogFooter>
                <Button onClick={() => setShowReset(false)} className="bg-[#0b1220] hover:bg-black text-white">Close</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={submitReset} className="space-y-3">
              <p className="text-[13px] text-[#374151]">
                Enter the email associated with your account. We'll email you a secure link to choose a new password.
              </p>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">Email</label>
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="h-11 bg-white border-[#e5e7eb] text-[#111827]"
                  data-testid="diag-login-reset-email"
                  autoFocus
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setShowReset(false)} className="border-[#e5e7eb]">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resetting}
                  className="bg-[#007a9e] hover:bg-[#00607d] text-white"
                  data-testid="diag-login-reset-submit"
                >
                  {resetting ? "Sending…" : "Send reset request"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiagnosticLogin;
